import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Select,
    MenuItem,
    Card,
    Stack,
    Paper,
    Divider,
    Alert,
    Pagination,
    Skeleton,
    SelectChangeEvent,
    useTheme,
    useMediaQuery,
    Menu,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import DataTable, { DataTableColumn } from '../../components/DataTable/DataTable';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import {
    verificationHistoryService,
    VerificationHistoryItem,
    VerificationHistoryDetail,
    VerificationHistoryUser,
} from '../../services/verificationSystemServices/verificationHistoryService';

const PAGE_SIZE = 20;

const SERVICE_OPTIONS = [
    'Business Online Enquiry',
    'Account Verification Service',
    'CIPC Report',
    'Consumer Trace',
    'Deeds Enquiry',
    'DO Verification System',
    'Director Enquiry Report',
    'Faceview Plus',
    'Realtime ID Verification',
    'Realtime Marriage Verification',
    'Telephone Trace',
    'Realtime IDV',
];

const statusColors: Record<string, { bg: string; color: string; dot: string }> = {
    SUCCESS:    { bg: '#D1FADF', color: '#027A48', dot: '#027A48' },
    COMPLETED:  { bg: '#D1FADF', color: '#027A48', dot: '#027A48' },
    FAILURE:    { bg: '#FEE2E2', color: '#DC2626', dot: '#DC2626' },
    ERROR:      { bg: '#FEE2E2', color: '#DC2626', dot: '#DC2626' },
    FAILED:     { bg: '#FEE2E2', color: '#DC2626', dot: '#DC2626' },
    PROCESSING: { bg: '#FFF1C0', color: '#A3871E', dot: '#CFAC2C' },
    PENDING:    { bg: '#FFF1C0', color: '#A3871E', dot: '#CFAC2C' },
};

const defaultStatusColor = { bg: '#ECECEC', color: '#555', dot: '#888' };

const getStatusStyle = (status: string | null) => {
    if (!status) return defaultStatusColor;
    return statusColors[status.toUpperCase()] ?? defaultStatusColor;
};

const formatDate = (val: string | null) => {
    if (!val) return '—';
    try {
        return new Date(val).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return val;
    }
};

const selectSx = {
    width: { xs: '100%', sm: '150px', md: '175px' },
    height: { xs: '42px', sm: '44px', md: '46px' },
    borderRadius: '52px',
    border: 'none',
    background: '#fff',
    fontSize: { xs: '12px', sm: '13px', md: '14px' },
    px: 2,
    fontFamily: 'Poppins, sans-serif',
    flexShrink: 0,
    '& .MuiSelect-select': {
        paddingRight: '32px !important',
        fontSize: { xs: '12px', sm: '13px', md: '14px' },
    },
    '& .MuiSelect-icon': {
        right: 10,
    },
    '& fieldset': { border: 'none' },
};

const VerificationHistory: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const dateFromRef = useRef<HTMLInputElement>(null);
    const dateToRef = useRef<HTMLInputElement>(null);

    const [items, setItems] = useState<VerificationHistoryItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const [serviceFilter, setServiceFilter] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [users, setUsers] = useState<VerificationHistoryUser[]>([]);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState<VerificationHistoryDetail | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);

    const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        verificationHistoryService.getUsers().then(setUsers).catch(() => {});
    }, []);

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const res = await verificationHistoryService.getHistory(page, PAGE_SIZE, {
                service: serviceFilter || undefined,
                userId: userFilter || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            });
            setItems(res.items);
            setTotal(res.total);
        } catch (err) {
            console.error('Failed to load history:', err);
        } finally {
            setLoading(false);
        }
    }, [page, serviceFilter, userFilter, dateFrom, dateTo]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    const resetPage = () => setPage(1);

    const handleServiceChange = (e: SelectChangeEvent) => {
        setServiceFilter(e.target.value);
        resetPage();
    };

    const handleUserChange = (e: SelectChangeEvent) => {
        setUserFilter(e.target.value);
        resetPage();
    };

    const handleViewDetail = async (item: VerificationHistoryItem) => {
        setDetailOpen(true);
        setDetailLoading(true);
        setDetail(null);
        setDetailError(null);
        try {
            const res = await verificationHistoryService.getDetail(item.request_id, item.service_name);
            setDetail(res);
        } catch (err: any) {
            setDetailError(err?.response?.data?.detail || 'Failed to load details');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleExport = async (format: 'pdf' | 'csv') => {
        setExportAnchorEl(null);
        setIsExporting(true);
        try {
            const blob = await verificationHistoryService.downloadHistory(format, {
                service: serviceFilter || undefined,
                userId: userFilter || undefined,
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `verification_history_${new Date().toISOString()}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err: any) {
            console.error('Export failed:', err);
        } finally {
            setIsExporting(false);
        }
    };

    const columns: DataTableColumn[] = [
        {
            key: 'created_at',
            label: 'Date',
            width: '180px',
            render: (val: string | null) => (
                <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#1a1a2e' }}>
                    {formatDate(val)}
                </Typography>
            ),
        },
        {
            key: 'service_name',
            label: 'Service',
            width: '200px',
            render: (val: string) => (
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#0A1D56' }}>
                    {val}
                </Typography>
            ),
        },
        {
            key: 'user_name',
            label: 'User',
            width: '160px',
            render: (val: string) => (
                <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#4a5568' }}>
                    {val}
                </Typography>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            width: '120px',
            render: (val: string | null) => {
                const sc = getStatusStyle(val);
                return (
                    <Chip
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: sc.dot, flexShrink: 0 }} />
                                <span>{val || 'N/A'}</span>
                            </Box>
                        }
                        size="small"
                        sx={{
                            backgroundColor: sc.bg,
                            color: sc.color,
                            fontWeight: 600,
                            fontSize: 12,
                            height: 28,
                            borderRadius: '24px',
                            px: 1,
                        }}
                    />
                );
            },
        },
        {
            key: 'request_id',
            label: 'Action',
            width: '100px',
            render: (_val: string, row: VerificationHistoryItem) => (
                <Button
                    variant="contained"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewDetail(row)}
                    sx={{
                        backgroundColor: '#0A1D56',
                        px: 4,
                        py: 1.5,
                        borderRadius: '12px',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: '#091B4C' },
                    }}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <VerificationPageLayout title="Verification History">
            {/* Filter bar — matches Approval page design */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    borderRadius: { xs: '16px', sm: '32px' },
                    backgroundColor: '#EEF2FF',
                    px: { xs: 1.5, sm: 2.5, md: 3 },
                    py: { xs: 1.5, sm: 1.75, md: 2 },
                    mb: 3,
                    gap: { xs: 1, sm: 2 },
                    boxSizing: 'border-box',
                    width: '100%',
                    overflow: 'hidden',
                }}
            >
                {/* Service filter */}
                <Select
                    size="small"
                    value={serviceFilter}
                    onChange={handleServiceChange}
                    displayEmpty
                    variant="standard"
                    disableUnderline
                    sx={selectSx}
                >
                    <MenuItem value="">All Services</MenuItem>
                    {SERVICE_OPTIONS.map((s) => (
                        <MenuItem key={s} value={s} sx={{ fontSize: '13px' }}>{s}</MenuItem>
                    ))}
                </Select>

                {/* User filter */}
                <Select
                    size="small"
                    value={userFilter}
                    onChange={handleUserChange}
                    displayEmpty
                    variant="standard"
                    disableUnderline
                    sx={selectSx}
                >
                    <MenuItem value="">All Users</MenuItem>
                    {users.map((u) => (
                        <MenuItem key={u.id} value={u.id} sx={{ fontSize: '13px' }}>{u.username}</MenuItem>
                    ))}
                </Select>

                {/* Date from */}
                <Box
                    onClick={() => dateFromRef.current?.showPicker?.()}
                    sx={{
                        background: '#fff',
                        borderRadius: '52px',
                        px: 2,
                        height: { xs: '42px', sm: '44px', md: '46px' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 170px' },
                        minWidth: 0,
                    }}
                >
                    <Typography sx={{ fontSize: '12px', color: '#888', fontFamily: 'Poppins, sans-serif', whiteSpace: 'nowrap' }}>From:</Typography>
                    <input
                        ref={dateFromRef}
                        type="date"
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); resetPage(); }}
                        style={{
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '13px',
                            color: dateFrom ? '#1a1a2e' : '#aaa',
                            width: '100%',
                            cursor: 'pointer',
                        }}
                    />
                </Box>

                {/* Date to */}
                <Box
                    onClick={() => dateToRef.current?.showPicker?.()}
                    sx={{
                        background: '#fff',
                        borderRadius: '52px',
                        px: 2,
                        height: { xs: '42px', sm: '44px', md: '46px' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 170px' },
                        minWidth: 0,
                    }}
                >
                    <Typography sx={{ fontSize: '12px', color: '#888', fontFamily: 'Poppins, sans-serif', whiteSpace: 'nowrap' }}>To:</Typography>
                    <input
                        ref={dateToRef}
                        type="date"
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); resetPage(); }}
                        style={{
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '13px',
                            color: dateTo ? '#1a1a2e' : '#aaa',
                            width: '100%',
                            cursor: 'pointer',
                        }}
                    />
                </Box>

                {/* Clear filters */}
                {(serviceFilter || userFilter || dateFrom || dateTo) && (
                    <Button
                        size="small"
                        onClick={() => { setServiceFilter(''); setUserFilter(''); setDateFrom(''); setDateTo(''); resetPage(); }}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            color: '#0A1D56',
                            fontSize: '13px',
                            fontFamily: 'Poppins, sans-serif',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Clear filters
                    </Button>
                )}

                {/* Result count and Export — pushed to right */}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#666',
                            fontSize: '13px',
                            fontFamily: 'Poppins, sans-serif',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {total} record{total !== 1 ? 's' : ''}
                    </Typography>

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={isExporting || total === 0}
                        onClick={(e) => setExportAnchorEl(e.currentTarget)}
                        startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <FileUploadOutlinedIcon />}
                        sx={{
                            borderColor: '#132F5F',
                            color: '#132F5F',
                            fontWeight: 600,
                            fontFamily: 'Poppins, sans-serif',
                            fontSize: '13px',
                            borderRadius: '12px',
                            textTransform: 'none',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                                borderColor: '#0A1D56',
                                backgroundColor: 'rgba(19, 47, 95, 0.04)',
                            }
                        }}
                    >
                        Export
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={() => setExportAnchorEl(null)}
                        PaperProps={{
                            elevation: 2,
                            sx: { borderRadius: '12px', minWidth: '120px', mt: 1 }
                        }}
                    >
                        <MenuItem onClick={() => handleExport('csv')} sx={{ fontSize: '13px', fontFamily: 'Poppins, sans-serif' }}>
                            CSV
                        </MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: '13px', fontFamily: 'Poppins, sans-serif' }}>
                            PDF
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            {/* Data — mobile cards or desktop table */}
            {loading ? (
                isMobile ? (
                    <Stack spacing={2}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: '12px' }} />
                        ))}
                    </Stack>
                ) : (
                    <DataTable columns={columns} data={[]} loading total={0} emptyMessage="" />
                )
            ) : items.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary" fontFamily="Poppins, sans-serif">No verification history found</Typography>
                </Box>
            ) : isMobile ? (
                <>
                    <Stack spacing={2}>
                        {items.map((item) => {
                            const sc = getStatusStyle(item.status);
                            return (
                                <Card
                                    key={item.request_id}
                                    elevation={3}
                                    sx={{
                                        borderRadius: 1,
                                        backgroundColor: '#FAF9F9',
                                        p: 2,
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                                        border: '2px solid #F2F0F0',
                                    }}
                                >
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Chip
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: sc.dot, flexShrink: 0 }} />
                                                        <span>{item.status || 'N/A'}</span>
                                                    </Box>
                                                }
                                                size="small"
                                                sx={{
                                                    backgroundColor: sc.bg,
                                                    color: sc.color,
                                                    fontWeight: 600,
                                                    fontSize: 12,
                                                    height: 28,
                                                    borderRadius: '24px',
                                                    px: 1,
                                                    fontFamily: 'Poppins, sans-serif',
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<VisibilityIcon sx={{ fontSize: '16px !important' }} />}
                                                onClick={() => handleViewDetail(item)}
                                                sx={{
                                                    backgroundColor: '#0A1D56',
                                                    borderRadius: '12px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: 12,
                                                    px: 2,
                                                    py: 0.75,
                                                    fontFamily: 'Poppins, sans-serif',
                                                    '&:hover': { backgroundColor: '#091B4C' },
                                                }}
                                            >
                                                View
                                            </Button>
                                        </Stack>

                                        <Divider />

                                        <Typography fontWeight={600} fontSize={14} fontFamily="Poppins, sans-serif" sx={{ color: '#0A1D56' }}>
                                            {item.service_name}
                                        </Typography>

                                        <Typography fontSize={12} fontFamily="Poppins, sans-serif" color="#4a5568">
                                            User: {item.user_name}
                                        </Typography>
                                        <Typography fontSize={12} fontFamily="Poppins, sans-serif" color="#666">
                                            {formatDate(item.created_at)}
                                        </Typography>
                                    </Stack>
                                </Card>
                            );
                        })}
                    </Stack>

                    {/* Mobile pagination */}
                    {total > PAGE_SIZE && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={Math.ceil(total / PAGE_SIZE)}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                size="small"
                                sx={{
                                    '& .Mui-selected': { backgroundColor: '#0A1D56', color: '#fff', '&:hover': { backgroundColor: '#091B4C' } },
                                }}
                            />
                        </Box>
                    )}
                </>
            ) : (
                <DataTable
                    columns={columns}
                    data={items}
                    total={total}
                    currentPage={page}
                    limit={PAGE_SIZE}
                    onPageChange={setPage}
                    emptyMessage="No verification history found"
                />
            )}

            {/* Detail dialog */}
            <Dialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '12px', maxHeight: '90vh' },
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#0A1D56',
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.5,
                        px: 3,
                    }}
                >
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                            {detail?.service_name || 'Verification Detail'}
                        </Typography>
                        {detail && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>
                                {formatDate(detail.created_at)} &middot; {detail.user_name}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={() => setDetailOpen(false)} sx={{ color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0 }}>
                    {detailLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: '#0A1D56' }} />
                        </Box>
                    )}

                    {detailError && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {detailError}
                        </Alert>
                    )}

                    {detail && !detailLoading && (
                        <Box>
                            {/* Meta row */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                    px: 3,
                                    py: 2,
                                    backgroundColor: '#FAFBFE',
                                    borderBottom: '1px solid #eef2f8',
                                }}
                            >
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem', textTransform: 'uppercase' }}>
                                        Request ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a2e', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                        {detail.request_id}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem', textTransform: 'uppercase' }}>
                                        Status
                                    </Typography>
                                    <Box>
                                        {(() => {
                                            const sc = getStatusStyle(detail.status);
                                            return (
                                                <Chip
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: sc.dot, flexShrink: 0 }} />
                                                            <span>{detail.status || 'N/A'}</span>
                                                        </Box>
                                                    }
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: sc.bg,
                                                        color: sc.color,
                                                        fontWeight: 600,
                                                        fontSize: 12,
                                                        height: 28,
                                                        borderRadius: '24px',
                                                        px: 1,
                                                    }}
                                                />
                                            );
                                        })()}
                                    </Box>
                                </Box>
                                {detail.error_message && (
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                        <Typography variant="caption" sx={{ color: '#888', fontSize: '0.68rem', textTransform: 'uppercase' }}>
                                            Error
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#c62828', fontSize: '0.8rem' }}>
                                            {detail.error_message}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Request data */}
                            {detail.request_data && Object.keys(detail.request_data).length > 0 && (
                                <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#0A1D56',
                                            fontSize: '0.8rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            mb: 1,
                                        }}
                                    >
                                        Request Data
                                    </Typography>
                                    <Paper variant="outlined" sx={{ border: '1px solid #dce6f5', borderRadius: '8px', px: 2, py: 1 }}>
                                        {Object.entries(detail.request_data).map(([key, val]) => (
                                            <Box
                                                key={key}
                                                sx={{
                                                    display: 'flex',
                                                    py: '6px',
                                                    borderBottom: '1px solid #f4f4f4',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#4a5568',
                                                        width: '40%',
                                                        flexShrink: 0,
                                                        fontSize: '0.78rem',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.03em',
                                                    }}
                                                >
                                                    {key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#1a1a2e', fontSize: '0.82rem', wordBreak: 'break-word' }}
                                                >
                                                    {val === null || val === undefined
                                                        ? '—'
                                                        : typeof val === 'object'
                                                        ? JSON.stringify(val)
                                                        : String(val)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Paper>
                                </Box>
                            )}

                            <Divider sx={{ my: 1 }} />

                            {/* Report data */}
                            {detail.report_data && Object.keys(detail.report_data).length > 0 ? (
                                <Box sx={{ px: 3, pb: 2 }}>
                                    <VerificationResultDisplay
                                        data={detail.report_data as Record<string, any>}
                                        title={detail.service_name}
                                        requestId={detail.request_id}
                                        success={detail.status?.toUpperCase() === 'SUCCESS' || detail.status?.toUpperCase() === 'COMPLETED'}
                                        message={detail.error_message || (detail.status === 'SUCCESS' ? 'Verification completed successfully' : `Status: ${detail.status || 'Unknown'}`)}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ px: 3, py: 3 }}>
                                    <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
                                        No report data available for this request.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button
                        onClick={() => setDetailOpen(false)}
                        variant="outlined"
                        sx={{
                            borderColor: '#0A1D56',
                            color: '#0A1D56',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { borderColor: '#091B4C', backgroundColor: 'rgba(10,29,86,0.04)' },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </VerificationPageLayout>
    );
};

export default VerificationHistory;
